import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, UtensilsCrossed, CheckCircle, Clock } from 'lucide-react';
import * as planService from '../../../services/planService';
import MacroTracker from './MacroTracker';

/**
 * Meal Plan View Component
 * Daily/weekly meal schedule with macro tracking
 */
export default function MealPlanView({ planId }) {
  const [meals, setMeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (planId) {
      loadMeals();
    }
  }, [planId, currentDate]);

  const loadMeals = async () => {
    setIsLoading(true);
    try {
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - startDate.getDay()); // Start of week
      
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6); // End of week

      const result = await planService.getMeals(planId, {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      });

      if (result.success) {
        setMeals(result.data || []);
      }
    } catch (error) {
      console.error('Error loading meals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWeekDates = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getMealsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return meals
      .filter((m) => m.scheduled_date === dateStr)
      .sort((a, b) => (a.meal_order || 0) - (b.meal_order || 0));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const getMealTypeColor = (type) => {
    switch (type) {
      case 'breakfast':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'lunch':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'dinner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'snack':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pre_workout':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'post_workout':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateDailyMacros = (dateMeals) => {
    return dateMeals.reduce(
      (acc, meal) => {
        const macros = meal.macros || {};
        return {
          calories: acc.calories + (macros.calories || 0),
          protein: acc.protein + (macros.protein || 0),
          carbs: acc.carbs + (macros.carbs || 0),
          fats: acc.fats + (macros.fats || 0),
        };
      },
      { calories: 0, protein: 0, carbs: 0, fats: 0 }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meal plans...</p>
        </div>
      </div>
    );
  }

  const weekDates = getWeekDates();
  const selectedDateMeals = getMealsForDate(new Date(selectedDate));
  const dailyMacros = calculateDailyMacros(selectedDateMeals);

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Meal Plan</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              viewMode === 'week'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to week view"
            aria-pressed={viewMode === 'week'}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('day')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation min-h-[44px] ${
              viewMode === 'day'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            type="button"
            aria-label="Switch to day view"
            aria-pressed={viewMode === 'day'}
          >
            Day
          </button>
        </div>
      </div>

      {/* Week Navigation */}
      {viewMode === 'week' && (
        <div className="flex items-center justify-between bg-white rounded-xl shadow-lg p-4">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <p className="text-sm text-gray-600">
              {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
              {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          </div>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Next week"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Today
          </button>
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="grid grid-cols-7 gap-2">
          {weekDates.map((date, index) => {
            const dateMeals = getMealsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const dateStr = date.toISOString().split('T')[0];

            return (
              <div
                key={index}
                className={`bg-white rounded-lg shadow p-3 min-h-[200px] cursor-pointer hover:shadow-lg transition-shadow ${
                  isToday ? 'ring-2 ring-primary-600' : ''
                }`}
                onClick={() => {
                  setSelectedDate(dateStr);
                  setViewMode('day');
                }}
              >
                <div className="text-center mb-2">
                  <p className="text-xs text-gray-500 uppercase">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </p>
                  <p
                    className={`text-lg font-bold ${
                      isToday ? 'text-primary-600' : 'text-gray-900'
                    }`}
                  >
                    {date.getDate()}
                  </p>
                </div>
                <div className="space-y-1">
                  {dateMeals.length > 0 ? (
                    dateMeals.slice(0, 3).map((meal) => (
                      <div
                        key={meal.id}
                        className={`text-xs p-2 rounded border ${getMealTypeColor(
                          meal.meal_type
                        )}`}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <UtensilsCrossed className="w-3 h-3" />
                          <span className="font-medium truncate">{meal.meal_name}</span>
                        </div>
                        {meal.macros?.calories && (
                          <div className="text-xs opacity-75">
                            {meal.macros.calories} cal
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-center text-gray-300 py-2">No meals</div>
                  )}
                  {dateMeals.length > 3 && (
                    <div className="text-xs text-center text-gray-400">
                      +{dateMeals.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="space-y-6">
          {/* Date Selector */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Daily Macros Summary */}
          <MacroTracker dailyMacros={dailyMacros} />

          {/* Meals for Selected Date */}
          <div className="space-y-4">
            {selectedDateMeals.length > 0 ? (
              selectedDateMeals.map((meal) => (
                <motion.div
                  key={meal.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${getMealTypeColor(
                    meal.meal_type
                  ).split(' ')[2]}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{meal.meal_name}</h3>
                      <p className="text-sm text-gray-600 capitalize">{meal.meal_type.replace('_', ' ')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {meal.prep_time_minutes && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{meal.prep_time_minutes} min</span>
                        </div>
                      )}
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <CheckCircle className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {meal.description && (
                    <p className="text-gray-700 mb-4">{meal.description}</p>
                  )}

                  {/* Macros */}
                  {meal.macros && Object.keys(meal.macros).length > 0 && (
                    <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                      {meal.macros.calories && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Calories</p>
                          <p className="text-lg font-bold text-gray-900">{meal.macros.calories}</p>
                        </div>
                      )}
                      {meal.macros.protein && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Protein</p>
                          <p className="text-lg font-bold text-gray-900">{meal.macros.protein}g</p>
                        </div>
                      )}
                      {meal.macros.carbs && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Carbs</p>
                          <p className="text-lg font-bold text-gray-900">{meal.macros.carbs}g</p>
                        </div>
                      )}
                      {meal.macros.fats && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Fats</p>
                          <p className="text-lg font-bold text-gray-900">{meal.macros.fats}g</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ingredients */}
                  {meal.ingredients && meal.ingredients.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Ingredients:</p>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {meal.ingredients.map((ingredient, idx) => (
                          <li key={idx}>
                            {typeof ingredient === 'string'
                              ? ingredient
                              : `${ingredient.amount || ''} ${ingredient.unit || ''} ${ingredient.name || ''}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Instructions */}
                  {meal.instructions && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Instructions:</p>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{meal.instructions}</p>
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No meals scheduled</h3>
                <p className="text-gray-600">No meals are scheduled for this date.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
